import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { ChangeDetectorRef, Component, Inject, Input, OnInit } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup
} from '@angular/forms';
import { BUTTON_TEXT, IFileInfo, PROGRESS_STATUS } from '@common/interfaces';
import { CommonHttpService } from 'libs/common/src/lib/service/common-http.service';
import { CommonService } from 'libs/common/src/lib/service/common.service';
import { Observable, Subject } from 'rxjs';

@Component({
  selector: 'app-confirmation-popup-with-form',
  templateUrl: './confirmation-popup-with-form.component.html',
  styleUrls: ['./confirmation-popup-with-form.component.scss'],
})
export class ConfirmationPopupWithFormComponent implements OnInit {
  fileTypes = [
    'application/pdf',
    'image/png',
    'image/jpg',
    'image/svg+xml',
    'image/jpeg',
  ];

  header = '';
  description = '';
  primaryBtn = '';
  showAddButton = true;
  secondaryBtn!: string;
  image = '';
  imageAddress = '';
  successImage = 'assets/48x48/Featured iconGreen-tick.svg';
  failureImage = 'assets/48x48/Featured iconred-excalmatory.svg';
  cautionImage = 'assets/48x48/logoutyellow-excalmatory.svg';
  confirmationForm!: FormGroup;
  fields: any[] = [];
  uploadInProgress: boolean = false;
  fileKeys: string[] = [];
  value: string = '';
  valuesArray: FormArray<FormControl>;
  unitsArray: FormArray<FormControl>;
  initialInputCount: number = 0;
  errors: string = '';
  initialUnitCount: number = 0;
  disableUpdateBtn = false;
  phoneError: string = '';
  nameError: string = '';
  isSaveButtonEnabled: boolean = true;
  isTextFieldDisabled: boolean = false;
  private addClientEmitter!: Subject<void>; 
  constructor(
    private commonHttpService: CommonHttpService,
    private commonService: CommonService,
    private dialogRef: DialogRef,
    public changeDetectorRef: ChangeDetectorRef,
    @Inject(FormBuilder) private fb: FormBuilder,
    @Inject(DIALOG_DATA)
    public data: {
      header: string;
      description: string;
      image: string;
      form: { confirmationForm: FormGroup };
      fields: any[];
      secondaryBtn: string;
      primaryBtn: string;
      isEditMode?: boolean;
      includeUnits?: boolean;
      includeIsRequired?: boolean;
      showAddButton?: boolean;
      isTextFieldDisabled?: boolean;
      loading?: boolean;
    }
  ) {
    // this.confirmationForm = this.fb.group({
    //   value: this.fb.array([this.fb.control('')]),
    //   unit: this.fb.array([this.fb.control('')]),
    // });
    this.confirmationForm = data.form?.confirmationForm ?? this.fb.group({});
    this.valuesArray = this.fb.array<FormControl>([
      this.fb.control(''),
    ]);
    this.unitsArray = this.fb.array<FormControl>([
      this.fb.control(''),
    ]);
  }
  ngOnInit(): void {
    this.header = this.data.header;
    this.description = this.data.description;
    this.image = this.data.image;
    this.imageAddress = this.getImageAddress(this.image);
    this.confirmationForm = this.data.form.confirmationForm;
    this.fields = this.data.fields;
    this.secondaryBtn = this.data.secondaryBtn;
    this.primaryBtn = this.data.primaryBtn;
    this.showAddButton = this.data.showAddButton ?? true;
    const includeUnits = this.data.includeUnits ?? true;
    const includeIsRequired = this.data.includeIsRequired ?? true;
    this.isTextFieldDisabled = this.data.isTextFieldDisabled ?? false;
    if (this.data.primaryBtn === 'Update') {
      this.disableUpdateBtn = !this.confirmationForm.dirty
    }

    if (this.data.primaryBtn === 'Save') {
      this.isSaveButtonEnabled = true;
    }

    // Check if 'text-fields' are present
    const hasTextFields = this.fields.some(
      (field) => field.field === 'text-fields'
    );

    const clientField = this.fields.find(field => field.isClient);
        if (clientField && clientField.addClientEmitter) {
            this.addClientEmitter = clientField.addClientEmitter;
        }

    if (hasTextFields) {
      const initialValues = (this.confirmationForm.get('value')?.value || '')
        .split(',')
        .map((v: string) => v.trim());

      this.valuesArray = this.fb.array(
        initialValues.map((val: string) =>
          this.fb.control(val)
        )
      );
      this.initialInputCount = this.valuesArray.length;
      this.confirmationForm.addControl('valuesArray', this.valuesArray);
      if (includeUnits) {
        const initialUnits = (this.confirmationForm.get('unit')?.value || '')
          .split(',')
          .map((u: string) => u.trim());
        this.unitsArray = this.fb.array(
          initialUnits.map((unit: string) =>
            this.fb.control(unit)
          )
        );
        this.initialUnitCount = this.unitsArray.length;
        this.confirmationForm.setControl('unitsArray', this.unitsArray);
      } else {
        if (this.confirmationForm.contains('unit')) {
          this.confirmationForm.removeControl('unit');
        }
        if (this.confirmationForm.contains('unitsArray')) {
          this.confirmationForm.removeControl('unitsArray');
        }
        this.unitsArray = this.fb.array([]);
      }
      if (includeIsRequired) {
        if (!this.confirmationForm.contains('isRequired')) {
          this.confirmationForm.addControl('isRequired', new FormControl(false));
        }
      } else {
        if (this.confirmationForm.contains('isRequired')) {
          this.confirmationForm.removeControl('isRequired');
        }
      }
    }
    this.syncFormControls(includeUnits);

    this.confirmationForm.statusChanges.subscribe((status) => { });
    this.confirmationForm.valueChanges.subscribe(() => { this.disableUpdateBtn = false });
    this.valuesArray.valueChanges.subscribe(() => { this.isSaveButtonEnabled = false });
  }
  get isAddButtonEnabled(): boolean {
    return this.valuesArray.controls.some(
      (control) => control.value.trim() !== ''
    );
  }
  get isUnitButtonEnabled(): boolean {
    return this.unitsArray.controls.some(
      (control) => control.value.trim() !== ''
    );
  }
  getImageAddress(imageType: string): string {
    switch (imageType) {
      case 'successImage':
        return this.successImage;
      case 'failureImage':
        return this.failureImage;
      case 'cautionImage':
        return this.cautionImage;
      default:
        return this.cautionImage;
    }
  }
  blockDigits(event: KeyboardEvent) {
    const allowedControlKeys = [
    'Backspace', 'Tab', 'Delete', 'ArrowLeft', 'ArrowRight',
    'ArrowUp', 'ArrowDown', 'Home', 'End', 'Shift', 'CapsLock',
    'Control', 'Alt', 'Meta'
  ];

  if (allowedControlKeys.includes(event.key)) {
    return;
  }

  const char = event.key;
  if (!/^[A-Za-z ]$/.test(char)) {
    event.preventDefault();
    this.nameError = 'Name cannot contain digits';
  } else {
    this.nameError = '';
  }
  }
  preventMoreThanTenDigits(event: KeyboardEvent) {
    const input = event.target as HTMLInputElement;
    const allowedKeys = ['Backspace', 'ArrowLeft', 'ArrowRight', 'Tab', 'Delete'];

    if (allowedKeys.includes(event.key)) {
      this.phoneError = '';
      return;
    }
    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
      this.phoneError = 'Phone Number can contain digits only';
      return;
    }
    if (input.value.length >= 10) {
      event.preventDefault();
      this.phoneError = 'Phone Number cannot be over 10 digits'
    }
  }
  // syncFormControls() {
  //   this.valuesArray.valueChanges.subscribe((values) => {
  //     const concatenatedValues = values.join(', '); // Combine all values as a single string
  //     this.confirmationForm
  //       .get('value')
  //       ?.setValue(concatenatedValues, { emitEvent: false });
  //   });

  //   this.unitsArray.valueChanges.subscribe((units) => {
  //     const concatenatedUnits = units.join(', '); // Combine all units as a single string
  //     this.confirmationForm
  //       .get('unit')
  //       ?.setValue(concatenatedUnits, { emitEvent: false });
  //   });
  // }
  syncFormControls(includeUnits: boolean) {
    this.valuesArray.valueChanges.subscribe((values) => {
      this.confirmationForm.get('value')?.setValue(values, { emitEvent: false });
    });

    if (includeUnits) {
      this.unitsArray.valueChanges.subscribe((units) => {
        this.confirmationForm.get('unit')?.setValue(units, { emitEvent: false });
      });
    }
  }

  onAddClientClick(): void {
    console.log('clickrd')
        if (this.addClientEmitter) {
            this.addClientEmitter.next();
        }
  }

  onClick(buttonText: string) {
    if (buttonText === 'Cancel') {
      this.closeDialog();
    } else if (buttonText === 'Save') {
      if (this.valuesArray.value.map((el) => el.trim() === '')) {
        this.dialogRef.close(this.confirmationForm.value);
      } else {
        this.confirmationForm.markAllAsTouched();
      }
    } else {
      if (this.fileKeys.length > 0)
        this.getOption(this.fileKeys, 'ticketClosureTempFileKey');
      this.dialogRef.close(this.confirmationForm.value);
    }
  }

  get controlForm(): FormGroup {
    return this.data.form.confirmationForm;
  }

  getControl(controlName: string) {
    return this.controlForm.get(controlName);
  }

  isInvalid(controlName: string): boolean {
    const control = this.getControl(controlName);
    return !!(control && control.invalid && (control.touched || control.dirty));
  }


  getOption(option: string | string[], control: string) {
    this.confirmationForm.controls[control].setValue(option);
  }
  getFormArray(controlName: string): FormArray {
    return this.confirmationForm.get(controlName) as FormArray;
  }

  addControl(controlName: string): void {
    if (controlName === 'value') {
      const newControl = this.fb.control('');
      this.valuesArray.push(newControl);
    } else if (controlName === 'unit' && this.data.includeUnits) {
      const newControl = this.fb.control('');
      this.unitsArray.push(newControl);
    }
  }
  removeValue(index: number): void {
    const control = this.valuesArray.at(index) as FormControl;
    if (index === 0) {
      control.setValue('');
      control.markAsPristine();
      control.markAsUntouched();
      control.updateValueAndValidity({ emitEvent: true });
      this.changeDetectorRef.detectChanges();
    } else {
      this.valuesArray.removeAt(index);
    }
  }

  removeUnit(index: number): void {
    if (this.data.includeUnits) {
      const control = this.unitsArray.at(index) as FormControl;
      if (index === 0) {
        control.setValue('');
        control.markAsPristine();
        control.markAsUntouched();
        control.updateValueAndValidity({ emitEvent: true });
        this.changeDetectorRef.detectChanges();
      } else {
        this.unitsArray.removeAt(index);
      }
    }
  }
  closeDialog() {
    this.dialogRef.close();
  }

  async selectedFiles(files: IFileInfo[]) {
    if (files.length) {
      for (const fileData of files) {
        await this.uploadFile([fileData]);
      }
    }
  }

  async uploadFile(files: IFileInfo[]) {
    this.uploadInProgress = true;

    for (let index = 0; index < files.length; index++) {
      const file = files[index];
      if (this.shouldUploadFile(file) && !this.isAlreadyUploaded(file)) {
        await this.uploadAndHandleFile(file);
      } else {
        files[index].showProgressBar = false;
      }
    }
  }

  shouldUploadFile(file: IFileInfo): boolean {
    return (
      file.progressStatus !== PROGRESS_STATUS.FAILED &&
      file.progressValue !== 100
    );
  }

  isAlreadyUploaded(file: IFileInfo): boolean {
    return file.progressValue === 100;
  }

  async uploadAndHandleFile(file: IFileInfo) {
    this.uploadInProgress = true;

    const subscription = this.commonService.getProgress().subscribe((value) => {
      file.progressValue = value;
      file.showRemoveFileBtn = false;
    });

    try {
      const fileInfo = [
        {
          originalname: file.originalname,
          mimetype: file.mimetype,
          size: file.size,
        },
      ];
      const loadRequest: Observable<Object> =
        this.commonHttpService.uploadFiles(fileInfo);
      this.uploadInProgress = false;
      subscription?.unsubscribe();
      loadRequest.subscribe({
        next: async (response: any) => {
          if (this.shouldUploadFile(file) && !this.isAlreadyUploaded(file)) {
            file.progressValue = 50;
            const s3response = await this.handleFileUpload(
              response.presignedUrls,
              [file]
            );

            if (response.presignedUrls.length === s3response.length) {
              file.progressValue = 100;
              file.showRemoveFileBtn = false;
              file.progressStatus = PROGRESS_STATUS.SUCCESS;

              const tempFileKeys = response.presignedUrls[0].key;
              this.fileKeys.push(tempFileKeys);
            }
          }
        },
        error: (error) => {
          file.showRemoveFileBtn = true;
          file.progressStatus = PROGRESS_STATUS.FAILED;
        },
      });
    } catch (error) {
    }
  }

  handleFileUpload(
    preSignedUrls: { key: string; presignedUrl: string }[],
    files: IFileInfo[]
  ) {
    return this.commonHttpService.uploadFilesToPresignedUrls(
      preSignedUrls,
      files
    );
  }
  getPrimaryBtnDisabled(btnText: string) {
    if (btnText === BUTTON_TEXT.SAVE || btnText === BUTTON_TEXT.ADD) {
      const firstTextField = this.fields.find(field => field.field === 'text-field');

      if (firstTextField && firstTextField.controlName) {
        const control = this.confirmationForm.get(firstTextField.controlName);
        return !control || !control.value || !control.value.trim();
      }
      return !this.valuesArray.controls.some(control => control.value?.trim());
    } else if (btnText === BUTTON_TEXT.UPDATE) {
      return this.disableUpdateBtn || this.confirmationForm.invalid
    }
    return this.confirmationForm.invalid
  }
 getErrorMessage(field: any): string {
  const control = this.confirmationForm.get(field.controlName);

  if (!control || !control.errors) {
    return '';
  }

  const errorKeys = Object.keys(control.errors);

  for (const errorKey of errorKeys) {
    const customMessage = field.errorMessages?.[errorKey];

    if (typeof customMessage === 'string') {
      return customMessage;
    }

    if (errorKey === 'minlength') {
      const required = control.errors['minlength']?.requiredLength;
      return `Minimum length is ${required}`;
    }

    if (errorKey === 'maxlength') {
      const required = control.errors['maxlength']?.requiredLength;
      return `Maximum length is ${required}`;
    }

    if (errorKey === 'pattern') {
      return 'Invalid format';
    }

    if(errorKey === 'required'){
      return 'This field is required'
    }
  }

  return field.error || 'Invalid input';
}



}
